import React, { useState, useEffect } from 'react'
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider, CssBaseline } from '@mui/material'
import theme from './theme/theme'
 
import Homepage from './components/Homepage'
import CheckWaterBills from './components/CheckWaterBills'
import CheckUnclaimedChecks from './components/CheckUnclaimedChecks'
import Login from './components/Login'
import SystemsPortal from './components/SystemsPortal'
import WaterBillingDashboard from './components/systems/water-billing/WaterBillingDashboard'
import RPTSystem from './components/systems/rpt/RPTSystem'
import RuralHealthDashboard from './components/systems/rural-health/RuralHealthDashboard'
import MunicipalCivilRegistryDashboard from './components/systems/civil-registry/MunicipalCivilRegistryDashboard'
import ELogBookDashboard from './components/systems/e-logbook/ELogBookDashboard'
import SettingsDashboard from './components/systems/settings/SettingsDashboard'
import BPLODashboard from './components/systems/bplo/BPLODashboard'
import ProtectedRoute from './components/ProtectedRoute'
import Notification from './components/Notification'
import ChatManager from './components/chat/ChatManager'
import { authService } from './authService'

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated())
  const [user, setUser] = useState(authService.getUserSession())

  // Listen for authentication changes
  useEffect(() => {
    // Check auth status on mount and when localStorage changes
    const checkAuth = () => {
      const isAuth = authService.isAuthenticated()
      setIsAuthenticated(isAuth)
      setUser(authService.getUserSession())
    }

    // Check immediately
    checkAuth()

    // Listen for storage events (for multi-tab sync)
    window.addEventListener('storage', checkAuth)
    
    // Custom event for same-tab auth changes
    window.addEventListener('authChange', checkAuth)

    return () => {
      window.removeEventListener('storage', checkAuth)
      window.removeEventListener('authChange', checkAuth)
    }
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div className="App">
          <Notification />
          {isAuthenticated && user && <ChatManager currentUser={user} />}
        <Routes>
          {/* Homepage - accessible to everyone */}
          <Route path="/" element={<Homepage />} />
          
          {/* Public Services */}
          <Route path="/check-water-bills" element={<CheckWaterBills />} />
          <Route path="/check-unclaimed-checks" element={<CheckUnclaimedChecks />} />
          
          {/* Login route */}
          <Route 
            path="/login" 
            element={
              isAuthenticated ? 
              <Navigate to="/portal" replace /> : 
              <Login onLoginSuccess={() => setIsAuthenticated(true)} />
            } 
          />
          
          {/* Protected Systems Portal */}
          <Route 
            path="/portal" 
            element={
              <ProtectedRoute>
                <SystemsPortal onLogout={() => setIsAuthenticated(false)} />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected Water Billing System routes */}
          <Route 
            path="/portal/water-billing/*" 
            element={
              <ProtectedRoute>
                <WaterBillingDashboard onLogout={() => setIsAuthenticated(false)} />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected RPT System routes */}
          <Route 
            path="/portal/property-tax/*" 
            element={
              <ProtectedRoute>
                <RPTSystem onLogout={() => setIsAuthenticated(false)} />
              </ProtectedRoute>
            } 
          />
          
          {/* Rural Health Unit routes - handles own authentication (login or access code) */}
          <Route 
            path="/portal/rural-health/*" 
            element={<RuralHealthDashboard onLogout={() => setIsAuthenticated(false)} />} 
          />
          
          {/* Municipal Civil Registry routes - handles own authentication (access code) */}
          <Route 
            path="/portal/civil-registry/*" 
            element={<MunicipalCivilRegistryDashboard onLogout={() => setIsAuthenticated(false)} />} 
          />
          
          {/* e-LogBook System routes */}
          <Route 
            path="/portal/e-logbook/*" 
            element={
              <ProtectedRoute>
                <ELogBookDashboard onLogout={() => setIsAuthenticated(false)} />
              </ProtectedRoute>
            } 
          />

          {/* Protected BPLO System routes */}
          <Route 
            path="/portal/business-permit/*" 
            element={
              <ProtectedRoute>
                <BPLODashboard onLogout={() => setIsAuthenticated(false)} />
              </ProtectedRoute>
            } 
          />
          
          {/* Settings System routes */}
          <Route 
            path="/portal/settings/*" 
            element={
              <ProtectedRoute>
                <SettingsDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      </Router>
    </ThemeProvider>
  )
}

export default App
